import useAuth from "./hooks/useAuth";
import { useState, useEffect } from "react";
import CheckUserLanguages from "./CheckUserLanguages";
import CheckUserDates from "./CheckUserDates";
import CheckUserSentences from "./CheckUserSentences";
import CheckUserPhonemes from "./CheckUserPhonemes";
import Select from 'react-select';
import CheckUserIntonation from "./CheckUserIntonation";

const CheckUserStats = ({ successHandler, checkuser }) => {
    const { auth } = useAuth();
    const stats = [
        { value: "Sentences", label: 'Sentences' },
        { value: "Phonemes", label: 'Phonemes' },
         { value: "Intonation", label: 'Intonation' }
    ];
    
    const [language, setLanguage] = useState();
    const [date, setDate] = useState('Select date');
    const [type, setType] = useState(stats[0]);
    const [representation, setRepresentation] = useState('Dynamic');
    const [autoSelected, setAutoSelected] = useState(false);

    const handleLanguageChange = (e) => {
        setLanguage(e.value);
        setDate('All records');
        setRepresentation('Dynamic');
        
        // Auto-select Sentences when language is chosen
        if (!autoSelected) {
            setAutoSelected(true);
        }
    };

    const handleDateChange = (e) => {
        setDate(e.value);
    };

  const handleTypeChange = (e) => {
    const newType = e.value;
    setType(e);
    
    // When switching to Sentences or Phonemes, set date to "All records" and use Dynamic view
    if (newType === 'Sentences' || newType === 'Phonemes') {
        setDate('All records');
        setRepresentation('Dynamic');
    } else if (newType === 'Intonation') {
        setDate('All records');
        setRepresentation('Comparison');
    } else {
        setDate('Select date');
    }
};

    // Determine if date selector should be disabled
 const isDateDisabled = !language || 
    (type?.value === 'Phonemes' && representation === 'Dynamic') || 
    (type?.value === 'Sentences' && (representation === 'Dynamic' || representation === 'Intensity')) ||
    (type?.value === 'Intonation' && representation === 'Comparison');

    // Check if we should show results (has valid date selection)
    const hasValidDate = date !== 'Select date';
    const hasValidFilters = language && 
    (
        ((type?.value === 'Sentences' || type?.value === 'Phonemes') && hasValidDate) ||
        (type?.value === 'Intonation' && (representation === 'Comparison' || hasValidDate))
    );

    // Auto-select Sentences when language is first chosen
    useEffect(() => {
        if (language && !autoSelected) {
            setAutoSelected(true);
        }
    }, [language, autoSelected]);

    // Get available representation options based on type
const getRepresentationOptions = () => {
    if (type?.value === 'Sentences') {
        return [
            { value: 'Dynamic', label: 'Dynamic View', description: 'All records, date selection disabled' },
            { value: 'Snapshot', label: 'Snapshot View', description: 'Select specific time period' },
            { value: 'Intensity', label: 'Intensity View', description: 'All records, date selection disabled' },
            { value: 'Trends', label: 'Trends View', description: 'All records, date selection disabled' }
        ];
    } else if (type?.value === 'Phonemes') {
        return [
            { value: 'Dynamic', label: 'Dynamic View', description: 'All records, date selection disabled' },
            { value: 'Snapshot', label: 'Snapshot View', description: 'Select specific time period' }
        ];
    } else if (type?.value === 'Intonation') {
        return [
            { value: 'Comparison', label: 'Comparison Analysis', description: 'Process and compare with narrator files' },
            { value: 'Snapshot', label: 'Snapshot View', description: 'View daily comparison results and audio pairs' }
        ];
    }
    return [];
};

   const handleRepresentationChange = (e) => {
    const newRepresentation = e.target.value;
    setRepresentation(newRepresentation);
    
    // Auto-set date based on representation
    if (newRepresentation === 'Dynamic' || newRepresentation === 'Intensity' || newRepresentation === 'Comparison'|| newRepresentation === 'Trends') {
        setDate('All records');
    } else {
        setDate('Select date'); // Reset to "Select date" for Snapshot view
    }
};

    const representationOptions = getRepresentationOptions();

    return (
        <div className="w-screen max-w-none relative left-1/2 right-1/2 -mx-[50vw] px-4 sm:px-6 lg:px-8 py-6">
            {/* Header with Back Button */}
            <div className="flex items-center justify-between mb-8">
                <button 
                    onClick={() => successHandler(false)}
                    className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors duration-200 font-medium"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                    Go back to users
                </button>
                <div className="text-center flex-1">
                    <h2 className="text-2xl font-bold text-blue-700">User Statistics</h2>
                    <p className="text-gray-600 mt-2">View progress and performance for {checkuser}</p>
                </div>
                <div className="w-32"></div> {/* Spacer for balance */}
            </div>

            {/* Filters Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center gap-2">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
                    </svg>
                    Filter Statistics
                </h3>
                
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Language Selector */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                            Language
                        </label>
                        <CheckUserLanguages 
                            checkuser={checkuser}
                            selectHandler={handleLanguageChange} 
                        />
                    </div>

                    {/* Date Selector */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            Time Period
                            {isDateDisabled && (
                                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Auto: All records</span>
                            )}
                        </label>
                        <CheckUserDates 
                            checkuser={checkuser}
                            language={language} 
                            selectHandler={handleDateChange}
                            disabled={isDateDisabled}
                            value={date}
                        />
                        {isDateDisabled && (
                            <p className="text-xs text-gray-500 mt-1">
                                Date selection is automatically set to "All records" for {type?.value === 'Sentences' && representation === 'Intensity' ? 'intensity' : 'dynamic'} analysis
                            </p>
                        )}
                    </div>

                    {/* Category Selector */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
                            <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                            Analysis Type
                        </label>
                        <Select 
                            onChange={handleTypeChange}
                            options={stats}
                            value={type}
                            className="text-sm"
                            styles={{
                                control: (base, state) => ({
                                    ...base,
                                    minHeight: '44px',
                                    borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                                    borderRadius: '8px',
                                    backgroundColor: 'white',
                                    boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                                    '&:hover': {
                                        borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
                                    },
                                    transition: 'all 0.2s ease-in-out'
                                }),
                                menu: (base) => ({
                                    ...base,
                                    borderRadius: '8px',
                                    border: '1px solid #e5e7eb',
                                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                                    zIndex: 50
                                })
                            }}
                        />
                    </div>
                </div>

{(type?.value === 'Sentences' || type?.value === 'Phonemes' || type?.value === 'Intonation') && (
    <div className="mt-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
        <label className="block text-sm font-medium text-purple-800 mb-2 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
            </svg>
            {type?.value === 'Intonation' ? 'Analysis Type' : 'View Type'}
        </label>
        
        {language ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {representationOptions.map((option) => (
                    <label key={option.value} className="flex items-start gap-2 p-3 bg-white rounded-lg border border-purple-100 hover:bg-purple-50 transition-colors cursor-pointer">
                        <input
                            type="radio"
                            value={option.value}
                            checked={representation === option.value}
                            onChange={handleRepresentationChange}
                            className="text-purple-600 focus:ring-purple-500 mt-0.5"
                        />
                        <div className="flex-1">
                            <div className="font-medium text-gray-800 text-sm">
                                {option.label}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                                {option.description}
                            </div>
                        </div>
                    </label>
                ))}
            </div>
        ) : (
            <div className="text-center py-4 bg-white rounded-lg border border-purple-100">
                <p className="text-gray-500 text-sm flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Select a language to choose {type?.value === 'Intonation' ? 'analysis type' : 'view type'}
                </p>
            </div>
        )}
    </div>
)}
                {/* Current Selection Display */}
               {hasValidFilters && (
    <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex flex-col sm:flex-row items-center gap-3 text-sm">
            <span className="font-semibold text-blue-800 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Current Analysis:
            </span>
            <div className="flex flex-wrap items-center gap-2">
                <span className="bg-white px-3 py-1 rounded-full border border-blue-200 font-medium text-blue-700 shadow-sm">
                    {type?.label}
                </span>
                {(type?.value === 'Sentences' || type?.value === 'Phonemes' || type?.value === 'Intonation') && (
                    <span className="bg-white px-3 py-1 rounded-full border border-purple-200 font-medium text-purple-700 shadow-sm">
                        {representation} {type?.value === 'Intonation' ? 'Analysis' : 'View'}
                    </span>
                )}
                <span className="text-blue-600 mx-1">•</span>
                <span className="bg-white px-3 py-1 rounded-full border border-blue-200 font-medium text-blue-700 shadow-sm">
                    {checkuser}
                </span>
                <span className="text-blue-600 mx-1">•</span>
                <span className="bg-white px-3 py-1 rounded-full border border-blue-200 font-medium text-blue-700 shadow-sm">
                    {language}
                </span>
                <span className="text-blue-600 mx-1">•</span>
                <span className="bg-white px-3 py-1 rounded-full border border-blue-200 font-medium text-blue-700 shadow-sm">
                    {date}
                </span>
            </div>
        </div>
    </div>
)}
            </div>
            

            {/* Results Section */}
            <div className="w-full">
                {hasValidFilters ? (
                    <div className="w-full">
                        {/* Content based on selected type */}
                        {type?.value === 'Sentences' && (
                            <CheckUserSentences 
                                checkuser={checkuser} 
                                language={language} 
                                date={date}
                                representation={representation}
                                key={`sentences-${checkuser}-${language}-${date}-${representation}`}
                            />
                        )}
                        
                        {type?.value === 'Phonemes' && (
                            <CheckUserPhonemes 
                                checkuser={checkuser} 
                                language={language} 
                                date={date}
                                representation={representation}
                                key={`phonemes-${checkuser}-${language}-${date}-${representation}`}
                            />
                        )}

                        {type?.value === 'Intonation' && (
                <CheckUserIntonation 
                    checkuser={checkuser} 
                    language={language} 
                    date={date}
                    representation={representation}
                    key={`intonation-${checkuser}-${language}-${date}-${representation}`}
                />
            )}
                    </div>
                ) : (
                    /* Empty State - Different states based on what's missing */
                    !language ? (
                        <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-200">
                            <div className="text-gray-400 text-6xl mb-4">👤</div>
                            <h3 className="text-xl font-semibold text-gray-600 mb-2">
                                Select Analysis Parameters
                            </h3>
                            <p className="text-gray-500 max-w-md mx-auto mb-6">
                                Choose a language to start analyzing statistics for {checkuser}
                            </p>
                            <div className="flex flex-col sm:flex-row justify-center gap-4 text-sm text-gray-500">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                                    Select target language
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                    Auto: All records
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                                    Auto: Sentences analysis
                                </div>
                            </div>
                        </div>
                    ) : !hasValidDate ? (
                        <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-200">
                            <div className="text-gray-400 text-6xl mb-4">📅</div>
                            <h3 className="text-xl font-semibold text-gray-600 mb-2">
                                Select Time Period
                            </h3>
                            <p className="text-gray-500 max-w-md mx-auto mb-6">
                                Choose a time period to view {language} {type?.label?.toLowerCase()} for {checkuser}
                            </p>
                            <div className="flex flex-col sm:flex-row justify-center gap-4 text-sm text-gray-500">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                    Select a specific time period
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                                    {type?.label} - {representation} View
                                </div>
                            </div>
                            {representation === 'Snapshot' && (
                                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200 max-w-md mx-auto">
                                    <p className="text-blue-600 text-sm flex items-center gap-2">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        <span><span className="font-semibold">{language}</span> and <span className="font-semibold">{type?.label}</span> selected. Now choose a time period to see the statistics.</span>
                                    </p>
                                </div>
                            )}
                        </div>
                    ) : (
                        /* This case should not happen with current logic, but keeping as fallback */
                        <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-200">
                            <div className="text-gray-400 text-6xl mb-4">📊</div>
                            <h3 className="text-xl font-semibold text-gray-600 mb-2">
                                Ready to Analyze
                            </h3>
                            <p className="text-gray-500 max-w-md mx-auto mb-6">
                                Configure your analysis settings to view statistics
                            </p>
                        </div>
                    )
                )}
            </div>
        </div>
    );
}

export default CheckUserStats;